import {
  BookingEvent,
  BookingEventIncidentReport,
  IncidentType,
} from '@bookings/interfaces/booking-event';
import { BookingEventModule } from '@bookings/store/booking-event.store';
import DrawerModal from '@shared/components/modals/drawer-modal/drawer-modal.vue';
import Vue from 'vue';
import BookingEventCancelModal from '@bookings/components/modals/booking-event-cancel-modal.vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import BookingEventForm from '../booking-event-form/booking-event-form.vue';
import { cloneDeep } from 'lodash';
import AddPaymentModal from '@accounting/components/modals/add-payment-modal/add-payment-modal.vue';
import BookingEventReportIncidentModal from '@bookings/components/modals/booking-event-report-incident-modal.vue';

import { library } from '@fortawesome/fontawesome-svg-core';
import { faClipboardList } from '@fortawesome/free-solid-svg-icons';

library.add(faClipboardList);

@Component({
  name: 'bpc-drawer-booking-event-form',
  components: {
    'bpc-drawer-modal': DrawerModal,
    'bpc-booking-event-form': BookingEventForm,
    'bpc-booking-event-cancel-modal': BookingEventCancelModal,
    'bc-add-payment-modal': AddPaymentModal,
    'bc-booking-event-report-incident-modal': BookingEventReportIncidentModal,
  },
})
export default class DrawerBookingEventForm extends Vue {
  $refs: {
    // resourceForm: InstanceType<typeof ResourceForm> & { saveEntity(): void };
    eventForm: InstanceType<typeof BookingEventForm> & {
      saveEntity(): void;
      model: BookingEvent;
      initializedFormModel: BookingEvent;
      onDeleteMediaFile(): void;
      searchEntity(): void;
      clickOnReplaceButton(): void;
      showSendEmailAfterChangeOption: boolean;
      showUpdateOrdersAfterChangeOption: boolean;
      showReplaceConflictsAfterChangeOption: boolean;
    };
  };
  /* Lifecycle hooks */
  // created () { }
  @Prop(Boolean) readonly showDrawer: boolean;
  @Prop([Number, String]) readonly entityId: number | string;
  @Prop(Object) readonly entity: BookingEvent;
  @Prop(Number) readonly bookingId: number;
  @Prop(Number) readonly contactId: number;
  @Prop(String) readonly title: string;
  @Prop({ type: String, default: 'default' }) readonly createFormConfigName: string;
  @Prop({ type: String, default: 'default-edit' }) readonly editFormConfigName: string;
  @Prop({ type: Number, default: 0 }) readonly deep: number;
  @Prop({ type: String, default: 'api' }) readonly mode: string;
  @Prop({ type: Boolean, default: false }) readonly withDraft: boolean;
  @Prop({ type: Boolean, default: false }) readonly disabled: boolean;

  // get storeName(): string {
  //   return this.entityId ? `bookingEvent_${this.entityId}` : 'bookingEvent';
  // }

  get storeName(): string {
    const initial = this.entityId ? `bookingEvent_${this.entityId}` : 'bookingEvent';
    return `${initial}_${this.deep}`;
  }

  public get showCancelButton(): boolean {
    const status = this.$store.getters[`${this.storeName}/model`]?.status;
    return (
      (status === 'draft' || status === 'confirmed') &&
      !this.$authService.isGuard() &&
      this.$authorizationService.hasPermission('manage_bookings')
    );
  }

  public get showAddIncidentButton(): boolean {
    return this.$authService.isGuard();
  }

  public get showRefuseButton(): boolean {
    return (
      this.$store.getters[`${this.storeName}/model`]?.status === 'requested' &&
      this.$authorizationService.hasPermission('manage_bookings')
    );
  }

  public get showConfirmButton(): boolean {
    return (
      (this.$store.getters[`${this.storeName}/model`]?.status === 'requested' ||
        this.$store.getters[`${this.storeName}/model`]?.status === 'draft') &&
      this.$authorizationService.hasPermission('manage_bookings')
    );
  }

  public get replaceButtonDisabled(): boolean {
    return this.$store.getters[`${this.storeName}/replaceButtonDisabled`];
  }

  public get realFormConfigName(): string {
    return this.entityId ? this.editFormConfigName : this.createFormConfigName;
  }

  public get showReplaceButton(): boolean {
    return this.$store.getters[`${this.storeName}/showReplaceButton`];
  }

  public get showSaveButton(): boolean {
    return this.$store.getters[`${this.storeName}/showSaveButton`];
  }

  public get saveButtonDisabled(): boolean {
    return this.$store.getters[`${this.storeName}/saveButtonDisabled`];
  }

  public get showSavingSpinner(): boolean {
    return this.$store.getters[`${this.storeName}/showSavingSpinner`];
  }

  public get confirmationUpdateModalTitle() {
    return this.$t('global.modals.update-booking-event.multiple.title');
  }

  public get confirmationUpdateModalBody() {
    return this.$t('global.modals.update-booking-event.body');
  }

  public get actionOptionsLabel() {
    return this.$t('global.modals.update-booking-event.multiple.action_option.label');
  }

  public get conflictOptionsLabel() {
    return this.$t('global.modals.update-booking-event.multiple.conflicts_case.label');
  }

  public get orderOptionsLabel() {
    return this.$t('global.modals.update-booking-event.multiple.orders.label');
  }

  public get sendEmailOptionsLabel() {
    return this.$t('global.modals.update-booking-event.send_email.label');
  }

  public get bookingRepetitive() {
    return this.$store.getters[`${this.storeName}/repetitive`];
  }

  public get noRefund() {
    return this.$store.getters[`${this.storeName}/noRefund`];
  }

  public get newPrice() {
    return this.$store.getters[`${this.storeName}/newPrice`];
  }

  public get originalPrice() {
    return this.$store.getters[`${this.storeName}/originalPrice`];
  }

  public get newForcedPrice() {
    return this.$store.getters[`${this.storeName}/newForcedPrice`];
  }

  public get bookingType() {
    return this.$store.getters[`${this.storeName}/bookingType`];
  }

  public get confirmButtonText() {
    return this.$t('global.buttons.confirm');
  }

  public get cancelButtonText() {
    return this.$t('global.buttons.cancel');
  }

  public get showUpdateOrdersAfterChangeOption() {
    if (this.$refs.eventForm) {
      return this.$refs.eventForm['article']?.['booking_price_type'] === 'booking'
        ? false
        : this.$refs.eventForm?.showUpdateOrdersAfterChangeOption;
    }
  }

  public get profileCanChangeOrders() {
    return this.$store.getters[`${this.storeName}/profileCanChangeOrders`];
  }

  public get defaultUpdateOrdersValue(): string {
    return this.$store.getters[`${this.storeName}/defaultUpdateOrdersValue`];
  }

  public get showReplaceConflictsAfterChangeOption() {
    return this.$refs.eventForm?.showReplaceConflictsAfterChangeOption;
  }

  public get showConfirmationUpdateModal() {
    if (this.bookingType === 'booking' && !this.bookingRepetitive) {
      return this.showUpdateOrdersAfterChangeOption;
    } else {
      return !!(this.bookingType === 'booking' && this.bookingRepetitive);
    }
  }

  get bookinEventIncidentReportData(): BookingEventIncidentReport {
    const bookingEvent = this.$store.getters[`${this.storeName}/model`];
    return {
      id: bookingEvent.id,
      real_number_of_participants: bookingEvent.real_number_of_participants,
      real_number_of_participants_description: bookingEvent.real_number_of_participants_description,
      incident_type: bookingEvent.incident_type
        ? bookingEvent.incident_type
        : IncidentType.InfrastructureDamage,
      incident_to_report: bookingEvent.incident_to_report,
      incident_description: bookingEvent.incident_description,
    };
  }

  public moduleReady: boolean = false;
  i: number = 1;
  showDrawerSection: boolean = false;
  showForm: boolean = false;
  actionOptionSelected: string = null;
  actionOptions: Record<string, any> = [];
  conflictOptionSelected: string = null;
  conflictOptions: Record<string, any> = [];
  orderOptionSelected: string = null;
  orderOptions: Record<string, any> = [];
  actionForm: string = null;
  sendEmailOptionSelected: boolean = this.defaultSendBookingEventChangeEmail;
  public showCancelBookingEventModal: boolean = false;
  public cancelBookingEventModalId = 'cancelBookingEventModal';
  public showReportIncidentEventModal: boolean = false;
  public showReportIncidentEventModalId: string = 'showReportIncidentEventModal';

  // Add payment modal
  public showPaymentModal: boolean = false;

  get showSendEmailOption(): boolean {
    return (
      this.$store.getters['centerSettings/centerSettings']?.send_booking_event_change_email &&
      this.$refs.eventForm?.showSendEmailAfterChangeOption
    );
  }

  get defaultSendBookingEventChangeEmail(): boolean {
    return !!this.$store.getters['centerSettings/centerSettings']
      ?.admin_send_booking_event_change_email;
  }

  public checkDeleteBookingAuth() {
    return this.$authorizationService.hasPermission('delete_bookings');
  }

  async beforeMount() {
    if (this.storeName !== 'bookingEvent' && !this.$store.hasModule(this.storeName)) {
      console.log('registering module', this.storeName);
      const mutations = cloneDeep(BookingEventModule.mutations);
      const getters = cloneDeep(BookingEventModule.getters);
      const state = cloneDeep(BookingEventModule.state);
      const actions = cloneDeep(BookingEventModule.actions);

      const mod = {
        namespaced: true,
        state,
        actions,
        mutations,
        getters,
      };
      await this.$store.registerModule(this.storeName, mod);
      console.log('module registered', this.storeName);
      this.$store.commit(`${this.storeName}/setMode`, this.mode);

      console.log('this.$store', this.$store);
    }
    this.moduleReady = true;
    // await this.$store.dispatch(`${this.storeName}/resetForm`);
    if (this.showDrawer) {
      this.showDrawerModal();
    }
  }

  public async destroyed() {
    await this.$store.dispatch(`${this.storeName}/resetForm`);

    // if (this.storeName !== 'bookingEvent' && this.$store.hasModule(this.storeName)) {
    //   this.$store.unregisterModule(this.storeName);
    // }
  }

  showDrawerModal() {
    this.showForm = false;
    this.$store.commit(`${this.storeName}/setIsDirty`, false);
    if (this.entityId) {
      this.$store.commit(`${this.storeName}/setCurrentAction`, 'edit'); // To indicate that we are not in the search of booking
    }
    this.showForm = true;
    this.showDrawerSection = true;
  }

  hideDrawerModal() {
    this.showForm = false;
    this.showDrawerSection = false;
    this.i++;
  }

  onCloseCancelBookingEventModal() {
    //this.$refs.provider.getEntities();
    this.showCancelBookingEventModal = false;
  }

  onCloseReportIncidentEventModal() {
    this.showReportIncidentEventModal = false;
  }
  onReportIncidentUpdated() {
    this.i++;
    this.onCloseReportIncidentEventModal();
    this.$bpcMessage.showSuccessMessage({ text: 'global.generic.successful_action' });
    this.i++; // refresh stdForm
    this.$emit('payment-created');
  }

  onCancelBookingEventConfirm() {
    this.$emit('booking-event-cancel');
    this.i++;
  }

  onFormSave() {
    this.actionForm = 'save';
    if (this.mode === 'api') {
      if (
        this.$refs.eventForm.model.status === 'cancelled' &&
        this.$refs.eventForm.initializedFormModel.status !== 'cancelled'
      ) {
        this.$bvModal.msgBoxOk(this.$t('global.use_corresponding_cancel_action') as string, {
          okTitle: this.$t('global.buttons.accept') as string,
          title: ((this.$t('global.modals.edit') as string) +
            ' ' +
            this.$t('global.models.booking.name')) as string,
        });
      } else {
        if (this.showConfirmationUpdateModal) {
          this.$bvModal.show(`confirmation-update-modal-${this.deep}-${this.entityId}`);
        } else {
          this.confirmUpdateEvent();
        }
      }
    } else {
      this.$refs.eventForm.saveEntity();
    }
  }

  onFormSaved(e) {
    this.$emit('entity-saved', e);
    this.i++;
  }

  onSavingEntity(e) {
    this.$emit('saving-entity', e);
  }

  onFormClose() {
    this.hideDrawerModal();
    this.$emit('form-closed');
  }

  onFormReplace() {
    this.actionForm = 'replace';
    if (this.mode === 'api') {
      this.$bvModal.show(`confirmation-update-modal-${this.deep}-${this.entityId}`);
    } else {
      this.$refs.eventForm.clickOnReplaceButton();
    }
  }

  confirmUpdateEvent() {
    // If the options are not available to select, set by default values to avoid the action
    if (!this.showReplaceConflictsAfterChangeOption) {
      this.conflictOptionSelected = 'do_nothing';
    }
    if (!this.showUpdateOrdersAfterChangeOption) {
      this.orderOptionSelected = 'no_update';
    }

    if (this.bookingRepetitive) {
      this.$store.commit(`${this.storeName}/setActionForUpdate`, this.actionOptionSelected);
      this.$store.commit(`${this.storeName}/setActionForConflicts`, this.conflictOptionSelected);
    }
    this.$store.commit(`${this.storeName}/setActionForOrders`, this.orderOptionSelected);
    this.$store.commit(`${this.storeName}/setNotifyByEmail`, this.sendEmailOptionSelected);

    if (this.actionForm === 'save') {
      this.$refs.eventForm.saveEntity();
    } else {
      this.$refs.eventForm.clickOnReplaceButton();
    }
  }

  resetConfirmationUpdateModal() {
    const status = this.$store.getters[`${this.storeName}/status`];
    this.actionOptions = [
      {
        text: this.$t('global.modals.update-booking-event.multiple.action_option.options.only'),
        value: 'only_this',
      },
      {
        text: this.$t(
          'global.modals.update-booking-event.multiple.action_option.options.next_same_events',
        ),
        value: 'next_same',
      },
      {
        text: this.$t(
          'global.modals.update-booking-event.multiple.action_option.options.all_same_events',
        ),
        value: 'all_same',
      },
      {
        text: this.$t(
          'global.modals.update-booking-event.multiple.action_option.options.all_events',
        ),
        value: 'cancel_all',
        disabled: status !== 'cancelled',
      },
    ];

    this.actionOptionSelected = 'only_this';

    this.conflictOptions = [
      {
        text: this.$t('global.modals.update-booking-event.multiple.conflicts_case.options.replace'),
        value: 'replace',
      },
      {
        text: this.$t(
          'global.modals.update-booking-event.multiple.conflicts_case.options.do_nothing',
        ),
        value: 'do_nothing',
      },
    ];

    this.conflictOptionSelected = 'replace';

    this.orderOptions = [
      {
        text: this.$t('global.modals.update-booking-event.multiple.orders.options.update'),
        value: 'update',
      },
      {
        text: this.$t('global.modals.update-booking-event.multiple.orders.options.no_update'),
        value: 'no_update',
      },
    ];

    this.orderOptionSelected = this.$store.getters[`${this.storeName}/defaultUpdateOrdersValue`];
  }

  @Watch('showDrawer')
  onShowChange() {
    if (this.showDrawer) {
      this.showDrawerModal();
    } else {
      this.hideDrawerModal();
    }
  }

  onFormEdit() {
    this.$emit('on-form-edit');
  }

  onOpenPaymentModal(entity) {
    this.showPaymentModal = true;
  }

  onCloseAddpaymentModal() {
    this.showPaymentModal = false;
  }

  onPaymentCreated() {
    this.showPaymentModal = false;
    this.$bpcMessage.showSuccessMessage({ text: 'global.generic.successful_action' });
    this.i++; // refresh stdForm
    this.$emit('payment-created');
  }

  get showExportToPosCheckoutButton(): boolean {
    return (
      this.$authorizationService.hasPermissionForAction('pos_sessions', 'pos_checkout', true) &&
      this.$store.getters['pos/openPosSession'] &&
      this.$store.getters[`${this.storeName}/orders`] &&
      this.$store.getters[`${this.storeName}/orders`].some(order => order.status === 'waiting')
    );
  }

  get showAddPaymentButton(): boolean {
    return (
      this.$authorizationService.hasPermission('manage_payments') &&
      this.$store.getters[`${this.storeName}/orders`] &&
      this.$store.getters[`${this.storeName}/orders`].some(order => order.status === 'waiting')
    );
  }

  get checkingPrice(): boolean {
    return this.$store.getters[`${this.storeName}/checkingPrice`];
  }

  onExportToPos() {
    const event = { item: this.$store.getters[`${this.storeName}/model`] };
    this.$emit('open-in-pos-checkout', event);
  }

  onActionReportIncident() {
    this.showReportIncidentEventModal = true;
  }

  onActionCancel() {
    //const event = { item: this.$store.getters[`${this.storeName}/model`] };
    //this.$emit('cancel', event);
    this.showCancelBookingEventModal = true;
  }

  onActionDelete() {
    const event = { item: this.$store.getters[`${this.storeName}/model`] };
    this.$emit('delete', event);
  }

  onActionRefuse() {
    this.$emit('refuse', this.$store.getters[`${this.storeName}/model`]);
  }

  onActionConfirm() {
    this.$emit('confirm', this.$store.getters[`${this.storeName}/model`]);
  }
  // updated () { }
  // destroyed () { }
}
